import type { Scene } from "@babylonjs/core/scene";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { DynamicTexture } from "@babylonjs/core/Materials/Textures/dynamicTexture";
import { RenderTargetTexture } from "@babylonjs/core/Materials/Textures/renderTargetTexture";
import { ReflectionProbe } from "@babylonjs/core/Probes/reflectionProbe";
import { Color3 } from "@babylonjs/core/Maths/math.color";

/** A reflection sky: colours from the top down to the horizon and below. */
export type ReflectionSky = [top: string, horizon: string, below: string];

/** The garage's: a dark studio with a bright softbox overhead. */
export const STUDIO_SKY: ReflectionSky = ["#3a4256", "#8c96ac", "#101218"];

/**
 * Something for the craft to reflect. Their paint and metal are PBR, and PBR
 * shows mostly as reflection of the surroundings, so with nothing to reflect
 * metal renders dark and gloss looks dull. This paints a sky of the given
 * colours, with a soft bright band high up that runs a highlight along a hull
 * like a studio softbox, renders it once into a small cube map, and makes that
 * the scene's environment. Only PBR materials use it, and only the craft are
 * PBR. Returns a disposer.
 */
export function buildShipReflections(scene: Scene, [top, horizon, below]: ReflectionSky): () => void {
  const W = 256;
  const H = 128;
  const tex = new DynamicTexture("reflSky", { width: W, height: H }, scene, false);
  const ctx = tex.getContext() as CanvasRenderingContext2D;
  const g = ctx.createLinearGradient(0, 0, 0, H);
  g.addColorStop(0, top);
  g.addColorStop(0.16, top);
  g.addColorStop(0.2, "#ffffff"); // the softbox band
  g.addColorStop(0.25, top);
  g.addColorStop(0.49, horizon);
  g.addColorStop(0.53, horizon);
  g.addColorStop(0.75, below);
  g.addColorStop(1, below);
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, W, H);
  tex.update();

  const sky = MeshBuilder.CreateSphere("reflSky", { diameter: 100, segments: 16, sideOrientation: Mesh.BACKSIDE }, scene);
  const mat = new StandardMaterial("reflSkyMat", scene);
  mat.disableLighting = true;
  mat.emissiveTexture = tex;
  mat.diffuseColor = new Color3(0, 0, 0);
  mat.specularColor = new Color3(0, 0, 0);
  mat.fogEnabled = false;
  mat.backFaceCulling = false;
  sky.material = mat;
  sky.isPickable = false;
  sky.setEnabled(false); // only ever drawn into the probe

  const probe = new ReflectionProbe("shipReflections", 128, scene, true);
  probe.refreshRate = RenderTargetTexture.REFRESHRATE_RENDER_ONCE;
  probe.renderList!.push(sky);
  probe.cubeTexture.onBeforeRenderObservable.add(() => sky.setEnabled(true));
  probe.cubeTexture.onAfterRenderObservable.add(() => sky.setEnabled(false));
  scene.environmentTexture = probe.cubeTexture;

  return () => {
    if (scene.environmentTexture === probe.cubeTexture) scene.environmentTexture = null;
    probe.dispose();
    sky.dispose(false, true);
  };
}
